import { useState, useEffect } from 'react';
import { fetchMembershipContent, submitMembershipForm } from './membershipApi';
import { BecomeMemberData, MembershipFormData } from './types';

export const useGetMembershipContent = () => {
  const [contentData, setContentData] = useState<BecomeMemberData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchMembershipContent();
        if (response.status === 200) {
          setContentData(response.data);
        } else {
          setError('Failed to load content');
        }
      } catch (err) {
        setError('Error loading content');
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, []);

  return { contentData, isLoading, error };
};

export const useSubmitMembershipForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitForm = async (formData: MembershipFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await submitMembershipForm(formData);

      // Check if the submission was successful
      if (response.succeeded && response.data.returnValue !== -1) {
        setIsSubmitted(true);
        return { success: true };
      } else {
        // Handle validation errors or other failures
        const errorMessage =
          response.data?.message || 'Form submission failed. Please try again.';
        setError(errorMessage);
        return { success: false };
      }
    } catch (err) {
      // Handle specific error messages from the API
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Error submitting form. Please try again.';
      setError(errorMessage);
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSubmission = () => {
    setIsSubmitted(false);
    setError(null);
  };

  return {
    submitForm,
    isSubmitting,
    isSubmitted,
    error,
    resetSubmission,
  };
};
